import { useState, useCallback } from 'react';
import { GitBranch, Plus, X, ChevronRight, Check, AlertCircle } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

// ─── Types ────────────────────────────────────────────────────

interface CareerLevel {
  level: number;
  title: string;
  description?: string;
  required_skills: string[];
  min_tenure_months: number;
  min_tenure_at_level_months: number;
}

interface CareerLadder {
  id: string;
  name: string;
  role_family: string;
  description?: string;
  levels: CareerLevel[];
  created_at: string;
}

interface LadderList {
  ladders: CareerLadder[];
}

interface ReadinessEmployee {
  employee_id: string;
  employee_name: string;
  title: string;
  department: string;
  ladder_name: string;
  current_level: number;
  current_level_title: string;
  next_level: number;
  next_level_title: string;
  readiness_percent: number;
  gap_count: number;
  need_skills: string[];
  tenure_eligible: boolean;
  tenure_months: number;
  min_tenure_months: number;
  promote_ready: boolean;
}

interface ReadinessData {
  employees: ReadinessEmployee[];
  total: number;
  promote_ready: number;
}

// ─── Components ───────────────────────────────────────────────

function ReadinessBadge({ pct, ready }: { pct: number; ready: boolean }) {
  const color = ready ? 'text-green-400' : pct >= 60 ? 'text-amber-400' : 'text-muted-foreground';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-16 h-1.5 bg-surface-3 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${ready ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-muted-foreground'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-medium ${color}`}>{pct}%</span>
    </div>
  );
}

// SVG step progress indicator for career path
function PathProgress({ levels, currentLevel }: { levels: CareerLevel[]; currentLevel: number }) {
  const n = levels.length;
  if (n === 0) return null;

  return (
    <div className="flex items-center gap-0">
      {levels.map((level, i) => {
        const isCompleted = level.level < currentLevel;
        const isCurrent = level.level === currentLevel;
        const isNext = level.level === currentLevel + 1;
        return (
          <div key={level.level} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  isCompleted ? 'bg-green-500 text-foreground' :
                  isCurrent ? 'bg-amber-500 text-foreground' :
                  isNext ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40' :
                  'bg-surface-3 text-muted-foreground'
                }`}
              >
                {isCompleted ? <Check className="w-3.5 h-3.5" /> : level.level}
              </div>
              <span className="text-xs text-muted-foreground max-w-[64px] text-center leading-tight">{level.title}</span>
            </div>
            {i < n - 1 && (
              <div className={`w-8 h-0.5 mb-5 ${isCompleted ? 'bg-green-500' : 'bg-surface-3'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Create ladder modal
function CreateLadderModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [roleFamily, setRoleFamily] = useState('');
  const [levels, setLevels] = useState<Array<{ title: string; required_skills: string; min_tenure_months: number }>>([
    { title: '', required_skills: '', min_tenure_months: 0 },
    { title: '', required_skills: '', min_tenure_months: 12 },
    { title: '', required_skills: '', min_tenure_months: 24 },
  ]);
  const [saving, setSaving] = useState(false);

  function addLevel() {
    setLevels(prev => [...prev, { title: '', required_skills: '', min_tenure_months: 0 }]);
  }

  function removeLevel(i: number) {
    setLevels(prev => prev.filter((_, idx) => idx !== i));
  }

  function updateLevel(i: number, field: string, value: string | number) {
    setLevels(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  async function create() {
    if (!name || !roleFamily || levels.some(l => !l.title)) return;
    setSaving(true);
    try {
      await fetch('/api/people/career-paths', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          role_family: roleFamily,
          levels: levels.map((l, i) => ({
            level: i + 1,
            title: l.title,
            required_skills: l.required_skills.split(',').map(s => s.trim()).filter(Boolean),
            min_tenure_months: l.min_tenure_months,
            min_tenure_at_level_months: 0,
          })),
        }),
      });
      onCreated();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-surface-1 border border-border rounded-2xl p-6 w-full max-w-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Create Career Ladder</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Ladder Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Technical Track"
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Role Family</label>
            <input value={roleFamily} onChange={e => setRoleFamily(e.target.value)} placeholder="e.g. IT Support"
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-muted-foreground">Levels ({levels.length})</label>
            <button onClick={addLevel} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add Level
            </button>
          </div>
          <div className="space-y-3">
            {levels.map((level, i) => (
              <div key={i} className="bg-surface-3 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Level {i + 1}</span>
                  {levels.length > 2 && (
                    <button onClick={() => removeLevel(i)} className="text-muted-foreground hover:text-red-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <input
                  value={level.title}
                  onChange={e => updateLevel(i, 'title', e.target.value)}
                  placeholder="Title (e.g. Junior Technician)"
                  className="w-full bg-surface-2 border border-border rounded px-2 py-1 text-xs text-foreground"
                />
                <input
                  value={level.required_skills}
                  onChange={e => updateLevel(i, 'required_skills', e.target.value)}
                  placeholder="Required skills (comma-separated)"
                  className="w-full bg-surface-2 border border-border rounded px-2 py-1 text-xs text-foreground"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground">Min tenure (months):</label>
                  <input
                    type="number"
                    min="0"
                    value={level.min_tenure_months}
                    onChange={e => updateLevel(i, 'min_tenure_months', parseInt(e.target.value) || 0)}
                    className="w-16 bg-surface-2 border border-border rounded px-2 py-1 text-xs text-foreground"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button
            onClick={() => void create()}
            disabled={saving || !name || !roleFamily || levels.some(l => !l.title)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" /> {saving ? 'Creating…' : 'Create Ladder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function CareerPathPage() {
  const [tab, setTab] = useState<'ladders' | 'readiness'>('ladders');
  const [showCreate, setShowCreate] = useState(false);
  const [expandedLadder, setExpandedLadder] = useState<string | null>(null);
  const { data: ladderData, loading: laddersLoading, refetch: refetchLadders } = useApi<LadderList>('people/career-paths');
  const { data: readinessData, loading: readinessLoading } = useApi<ReadinessData>('people/career-paths/promote-readiness');

  const handleCreated = useCallback(() => {
    setShowCreate(false);
    void refetchLadders();
  }, [refetchLadders]);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Career Path Engine</h1>
            <p className="text-sm text-muted-foreground">Career ladders, skill gaps, and promotion readiness</p>
          </div>
        </div>
        {tab === 'ladders' && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm"
          >
            <Plus className="w-3.5 h-3.5" /> New Ladder
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-border">
        {[{ id: 'ladders' as const, label: 'Career Ladders' }, { id: 'readiness' as const, label: 'Promotion Readiness' }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-amber-500 text-amber-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'ladders' && (
        <>
          {laddersLoading ? (
            <div className="text-muted-foreground text-sm">Loading ladders…</div>
          ) : ladderData?.ladders.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <GitBranch className="w-10 h-10 text-indigo-500/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No career ladders defined. Create one to start mapping growth paths.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ladderData?.ladders.map(ladder => (
                <div key={ladder.id} className="glass rounded-xl p-5 space-y-3">
                  <button
                    className="flex items-center justify-between w-full text-left"
                    onClick={() => setExpandedLadder(expandedLadder === ladder.id ? null : ladder.id)}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{ladder.name}</span>
                        <span className="text-xs text-muted-foreground px-1.5 py-0.5 bg-surface-3 rounded">{ladder.role_family}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{ladder.levels.length} levels</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedLadder === ladder.id ? 'rotate-90' : ''}`} />
                  </button>

                  {expandedLadder === ladder.id && (
                    <div className="pt-3 border-t border-border/50 space-y-4">
                      <div className="overflow-x-auto pb-2">
                        <PathProgress levels={ladder.levels} currentLevel={0} />
                      </div>
                      <div className="space-y-3">
                        {ladder.levels.map(level => (
                          <div key={level.level} className="p-3 bg-surface-3 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center">
                                  {level.level}
                                </span>
                                <span className="text-sm font-medium text-foreground">{level.title}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">Min {level.min_tenure_months}mo tenure</span>
                            </div>
                            {level.required_skills.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {level.required_skills.map(skill => (
                                  <span key={skill} className="px-2 py-0.5 bg-surface-2 rounded text-xs text-muted-foreground border border-border">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'readiness' && (
        <>
          {readinessLoading ? (
            <div className="text-muted-foreground text-sm">Loading readiness data…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="glass rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-1">Employees on Ladders</p>
                  <p className="text-2xl font-bold text-foreground">{readinessData?.total ?? 0}</p>
                </div>
                <div className="glass rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-1">Promote Ready</p>
                  <p className={`text-2xl font-bold ${(readinessData?.promote_ready ?? 0) > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {readinessData?.promote_ready ?? 0}
                  </p>
                </div>
              </div>

              {readinessData?.employees.length === 0 ? (
                <div className="glass rounded-xl p-8 text-center">
                  <AlertCircle className="w-10 h-10 text-amber-500/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No employees assigned to career ladders yet.</p>
                </div>
              ) : (
                <div className="glass rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Employee</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Current Level</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Next Level</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Readiness</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Gap</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readinessData?.employees.map(emp => (
                        <tr key={emp.employee_id} className="border-b border-border/50 hover:bg-surface-3/30">
                          <td className="p-3">
                            <p className="font-medium text-foreground">{emp.employee_name}</p>
                            <p className="text-xs text-muted-foreground">{emp.department}</p>
                          </td>
                          <td className="p-3">
                            <p className="text-foreground">L{emp.current_level}: {emp.current_level_title}</p>
                            <p className="text-xs text-muted-foreground">{emp.ladder_name}</p>
                          </td>
                          <td className="p-3 text-muted-foreground">
                            L{emp.next_level}: {emp.next_level_title}
                          </td>
                          <td className="p-3 text-right">
                            <ReadinessBadge pct={emp.readiness_percent} ready={emp.promote_ready} />
                          </td>
                          <td className="p-3 text-right">
                            {emp.gap_count > 0 ? (
                              <span className="text-xs text-amber-400">{emp.gap_count} skills</span>
                            ) : (
                              <span className="text-xs text-green-400">No gaps</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            {emp.promote_ready ? (
                              <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">Ready</span>
                            ) : !emp.tenure_eligible ? (
                              <span className="px-2 py-0.5 rounded text-xs bg-muted/20 text-muted-foreground">Tenure</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">In Progress</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {showCreate && (
        <CreateLadderModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}
    </div>
  );
}
